import type { PanelImperativeHandle, PanelSize } from "react-resizable-panels";

import { type RefObject, useCallback, useLayoutEffect } from "react";

interface UsePanelCollapseProps {
    panelRef: RefObject<PanelImperativeHandle | null>;
    isCollapsed: boolean | undefined;
}

export const usePanelCollapse = ({ panelRef, isCollapsed }: UsePanelCollapseProps) => {
    useLayoutEffect(() => {
        try {
            panelRef.current?.[isCollapsed ? "collapse" : "expand"]();
        } catch (error) {
            console.error(`Error toggling panel:`, error);
        }
    }, [isCollapsed, panelRef]);

    // v4 auto-collapses collapsible panels when they shrink below minSize (e.g. during
    // a fast browser window resize). This handler immediately re-expands the panel
    // when the consumer hasn't requested collapse, preventing the auto-collapse from
    // taking visible effect.
    const onResize = useCallback(
        (panelSize: PanelSize) => {
            if (isCollapsed) return; // consumer wants it collapsed — don't fight it
            const panel = panelRef.current;
            if (!panel) return;
            if (panelSize.asPercentage === 0 && panel.isCollapsed()) {
                panel.expand();
            }
        },
        [isCollapsed, panelRef],
    );

    return { onResize };
};
